import { useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Easing, Platform, useWindowDimensions, View } from 'react-native';
import { Listing } from 'shared/@types';
import { LikedListingCard } from 'shared/components/liked-listing-card';
import { useDevice } from 'shared/library/use-device';

type CardLayout = {
  key: string;
  width: number;
  top: number;
  left: number;
  rotate: string;
  zIndex?: number;
};

const CANVAS_WIDTH = 680;
const CANVAS_HEIGHT = 540;
const CANVAS_LIFT = -46;
const CARD_ENTRANCE_STAGGER = 85;
const NATIVE_MAX_SCALE = 0.52;
const NATIVE_HORIZONTAL_INSET = 24;

const cards: CardLayout[] = [
  { key: 'hero-main', left: 230, top: 104, width: 220, rotate: '0deg', zIndex: 20 },
  { key: 'hero-top-left', left: 88, top: 38, width: 170, rotate: '-7deg', zIndex: 10 },
  { key: 'hero-top-right', left: 422, top: 38, width: 170, rotate: '7deg', zIndex: 10 },
  { key: 'hero-mid-left', left: 24, top: 232, width: 148, rotate: '-4deg', zIndex: 12 },
  { key: 'hero-mid-right', left: 508, top: 232, width: 148, rotate: '4deg', zIndex: 12 },
  { key: 'hero-bottom-left', left: 170, top: 336, width: 146, rotate: '-3deg', zIndex: 8 },
  { key: 'hero-bottom-right', left: 364, top: 336, width: 146, rotate: '3deg', zIndex: 8 },
];

const AnimatedView = Animated.createAnimatedComponent(View);

const ScaledCanvas = ({ listings, scale }: { listings: Listing[]; scale: number }) => {
  const cardAnimations = useRef(
    cards.map(() => ({
      opacity: new Animated.Value(0),
      translateY: new Animated.Value(26),
    })),
  ).current;

  useEffect(() => {
    const sequence = cards.map((_, index) =>
      Animated.parallel([
        Animated.timing(cardAnimations[index]!.opacity, {
          toValue: 1,
          duration: 220,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
        Animated.timing(cardAnimations[index]!.translateY, {
          toValue: 0,
          duration: 340,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
      ]),
    );

    Animated.stagger(CARD_ENTRANCE_STAGGER, sequence).start();
  }, [cardAnimations]);

  const getListing = (index: number) => listings[index]!;

  return (
    <View
      className={'relative'}
      style={{
        width: CANVAS_WIDTH * scale,
        height: CANVAS_HEIGHT * scale,
      }}
    >
      <AnimatedView
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: CANVAS_WIDTH,
          height: CANVAS_HEIGHT,
          transformOrigin: 'top left',
          transform: [{ translateY: CANVAS_LIFT }, { scale }],
        }}
      >
        {cards.map((card, index) => (
          <AnimatedView
            key={card.key}
            style={{
              position: 'absolute',
              left: card.left,
              top: card.top,
              width: card.width,
              zIndex: card.zIndex,
              opacity: cardAnimations[index]!.opacity,
              transform: [
                { translateY: cardAnimations[index]!.translateY },
                { rotate: card.rotate },
              ],
            }}
          >
            <LikedListingCard listing={getListing(index)} />
          </AnimatedView>
        ))}
      </AnimatedView>
    </View>
  );
};

const HeroListingsCluster = ({ listings }: { listings: Listing[] }) => {
  const { widthBreakpoint } = useDevice();
  const { width: windowWidth } = useWindowDimensions();
  const [mounted, setMounted] = useState(false);
  const scale = useMemo(() => {
    if (Platform.OS !== 'web') {
      return Math.min(
        NATIVE_MAX_SCALE,
        Math.max(0, (windowWidth - NATIVE_HORIZONTAL_INSET) / CANVAS_WIDTH),
      );
    }

    switch (widthBreakpoint) {
      case 'xl':
        return 1;
      case 'lg':
        return 0.84;
      case 'md':
        return 0.72;
      case 'sm':
        return 0.58;
      default:
        return 0.52;
    }
  }, [widthBreakpoint, windowWidth]);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (listings.length === 0) {
    return null;
  }

  return (
    <View
      className={'relative overflow-visible px-0 pb-0 pt-0 sm:px-1 md:px-6 md:pb-6 md:pt-0'}
      testID={'hero-listings-cluster'}
    >
      <View className={'absolute inset-0'}>
        <View
          className={
            'absolute left-1/2 top-[42%] size-[22rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/70 blur-3xl md:size-[28rem]'
          }
        />
        <View
          className={
            'absolute left-[28%] top-[16%] size-24 rounded-full bg-white/55 blur-2xl md:size-32'
          }
        />
        <View
          className={
            'absolute bottom-[20%] right-[20%] size-28 rounded-full bg-white/45 blur-2xl md:size-36'
          }
        />
      </View>

      <View className={'items-center'}>
        <View
          className={
            Platform.OS === 'web'
              ? 'relative h-[14.5rem] w-[21.75rem] sm:h-[16.75rem] sm:w-[24.75rem] md:h-[21.5rem] md:w-[30.75rem] lg:h-[25.5rem] lg:w-[36rem] xl:h-[31rem] xl:w-[42.5rem]'
              : 'relative'
          }
          style={
            Platform.OS === 'web'
              ? undefined
              : {
                  width: CANVAS_WIDTH * scale,
                  height: (CANVAS_HEIGHT + CANVAS_LIFT) * scale,
                }
          }
        >
          {mounted ? <ScaledCanvas listings={listings} scale={scale} /> : null}
        </View>
      </View>
    </View>
  );
};

export { HeroListingsCluster };
